import React, { lazy, Suspense, useMemo } from 'react';

import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { EmojiIDNotFoundOverlay } from '@pkg/components';
import YatLoader from '@pkg/components/Sections/Profile/Loader';
import { useAppSelector } from '@pkg/hooks';

import { YatPageVersion } from '@app/client/common/structs/yatPage/yat-page';
import { useProfilePageInit } from '@app/pages/ProfilePage/hooks';
import { usePrefetchSections } from '@app/pages/ProfilePage/hooks/usePrefetchSections';
import { useCheckPublicRedirects } from '@app/routes/hooks';

import { useSentryRoutes } from '../hooks/useSentryRoutes';

// const PageNotFound = lazy(() => import('@app/pages/PageNotFound').then((module) => ({ default: module.PageNotFound })));

const YatPage = lazy(() => import('@app/pages/YatPage'));
const NFTShareablePage = lazy(() => import('@app/pages/NFTShareable'));

const ProfilePage = lazy(() => import('@app/pages/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const NFTSection = lazy(() => import('@app/pages/ProfilePage/sections/NFTSection'));
const LinksSection = lazy(() =>
    import('@app/pages/ProfilePage/sections/LinksSection').then((module) => ({ default: module.LinksSection })),
);
const YatsSection = lazy(() => import('@app/pages/ProfilePage/sections/YatsSection'));
const WishlistsSection = lazy(() =>
    import('@app/pages/ProfilePage/sections/WishlistsSection').then((module) => ({
        default: module.WishlistsSection,
    })),
);
const HistorySection = lazy(() =>
    import('@app/pages/ProfilePage/sections/HistorySection').then((module) => ({ default: module.HistorySection })),
);
const OffersSection = lazy(() =>
    import('@app/pages/ProfilePage/sections/OffersSection').then((module) => ({ default: module.OffersSection })),
);
const LiquidIDSection = lazy(() => import('@app/pages/ProfilePage/sections/LiquidIDSection/LiquidIDSection2'));

export enum PortfolioRoutes {
    NftSection = '',
    LinksSection = 'links',
    WishlistsSection = 'wishlists',
    YatsSection = 'yats',
    HistorySection = 'history',
    OffersSection = 'orders',
    LiquidIDSection = 'liquidID',
}

function ProfileSectionRoutes() {
    const location = useLocation();
    const { marketplaceV1Enabled, yatsPortfolio, showCSSLiquidID } = useAppSelector(
        (state) => state.featureFlags.features,
    );
    const {
        nftsEnabled,
        wishlistsEnabled,
        linksEnabled,
        isLoading,
        isLoadingLinks,
        isLoadingWishlists,
        isLoadingItems,
    } = usePrefetchSections();

    const currentPath = location.pathname;
    const replaceLastPath = (newPath: string) => {
        return currentPath.replace(/[^/]*$/, newPath) + location.search;
    };
    const sections = useSentryRoutes([
        {
            index: true,
            element:
                nftsEnabled || isLoading ? (
                    <NFTSection>{isLoadingItems && <YatLoader show />}</NFTSection>
                ) : (
                    <Navigate to={currentPath + `/${PortfolioRoutes.LinksSection}${location.search}`} />
                ),
        },
        {
            path: PortfolioRoutes.LinksSection,
            element:
                linksEnabled || isLoadingLinks ? (
                    <LinksSection />
                ) : (
                    <Navigate to={replaceLastPath(PortfolioRoutes.WishlistsSection)} />
                ),
        },
        {
            path: PortfolioRoutes.WishlistsSection,
            element:
                wishlistsEnabled || isLoadingWishlists ? (
                    <WishlistsSection />
                ) : (
                    <Navigate to={replaceLastPath(PortfolioRoutes.YatsSection)} />
                ),
        },
        // Yats, History, and Offers all rely on `marketplace`.
        {
            path: PortfolioRoutes.YatsSection,
            element: yatsPortfolio ? <YatsSection /> : <Navigate to={'/'} />,
        },
        { path: PortfolioRoutes.HistorySection, element: marketplaceV1Enabled ? <HistorySection /> : <NFTSection /> },
        { path: PortfolioRoutes.OffersSection, element: marketplaceV1Enabled ? <OffersSection /> : <NFTSection /> },
        { path: PortfolioRoutes.LiquidIDSection, element: showCSSLiquidID ? <LiquidIDSection /> : <NFTSection /> },
        {
            path: '*',
            element: isLoading ? <YatLoader show /> : <Navigate to={replaceLastPath(PortfolioRoutes.YatsSection)} />,
        },
    ]);

    return <Suspense>{sections}</Suspense>;
}

function SuspenseOutlet() {
    return (
        <>
            <Outlet />
            <Suspense fallback={<div />} />
        </>
    );
}

const legacyPageRoutes = ['edit', 'editlinks', 'go'];

export const EmojiIDRoutes = () => {
    const primaryYat = useAppSelector((state) => state.user.primaryYat);
    const { redirectTo } = useCheckPublicRedirects();
    const { isNotFound, pageVersion, eid, isLoading, isValidEmojiId } = useProfilePageInit();

    const isV1 = eid !== primaryYat || pageVersion === YatPageVersion.V1;
    const catchAllElement = useMemo(() => {
        if (isLoading) {
            return <YatLoader />;
        }
        if (isNotFound) {
            return <EmojiIDNotFoundOverlay emojiId={eid} />;
        }
        return null;
    }, [eid, isLoading, isNotFound]);

    const profileRoutes = useMemo(() => <Route path="*" element={<ProfileSectionRoutes />} />, []);

    return (
        <Routes>
            <Route element={<SuspenseOutlet />}>
                <Route path="nft-shareable" element={<NFTShareablePage />} />
                {isLoading ? null : (
                    <Route element={redirectTo ? <Navigate to={redirectTo} /> : <SuspenseOutlet />}>
                        {isV1 && legacyPageRoutes.map((path) => <Route key={path} path={path} element={<YatPage />} />)}
                    </Route>
                )}
                <Route
                    element={
                        <ProfilePage
                            isValidEmojiId={isValidEmojiId}
                            eid={eid}
                            pageVersion={pageVersion}
                            isNotFound={isNotFound}
                        />
                    }
                >
                    {profileRoutes}
                </Route>
            </Route>

            <Route path="*" element={catchAllElement} />
        </Routes>
    );
};
